import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import swal from 'sweetalert';
import LoginHeaderOperational from './LoginHeaderOperational';
import Sidebar_Progress from '../layouts/Operational_Sidebar_Progress.component';
import { API_URL } from '../../constant/API_Settings';
import { fetchReportJson, reportNumberFormat, downloadReportPdf, formatReportDate, formatReportDateRange } from '../../utils/reportFormat';
import {
  OtherReportActionButtons,
  ReportDateRangeFields,
  OtherReportDocument,
  OtherReportEmpty,
  OtherReportFilterCard,
  OtherReportFilterSection,
  OtherReportLoader,
  OtherReportPageHeader,
  OtherReportPlaceholder,
  OtherReportResultsZone,
  OtherReportSearchableCheckList,
  OtherReportShell,
  OtherReportTable,
  OtherReportToolbar,
} from './other-reports/OtherReportShared';

const OtherIncomeReport = (props) => {
  const location = useLocation();
  const isOtherReportsRoute = location.pathname.startsWith('/other-reports/');
  const [meta, setMeta] = useState({ income_heads: [] });
  const [inputValues, setInputValues] = useState({ income: [] });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    fetchReportJson(`${API_URL}/other_income_statement/meta`)
      .then(setMeta)
      .catch((error) => swal('Other Income Report', error.message, 'error'));
  }, []);

  const toggleIncome = (id) => {
    const selected = new Set((inputValues.income || []).map(String));
    const key = String(id);
    if (selected.has(key)) {
      selected.delete(key);
    } else {
      selected.add(key);
    }
    setInputValues({ ...inputValues, income: Array.from(selected) });
  };

  const setIncome = (ids) => {
    setInputValues({ ...inputValues, income: (ids || []).map(String) });
  };

  const loadReport = async () => {
    if (!inputValues.start_date || !inputValues.end_date) {
      swal('Please select start and end dates', '', 'warning');
      return;
    }
    if (!inputValues.income?.length) {
      swal('Please select at least one income head', '', 'warning');
      return;
    }

    setLoading(true);
    setReport(null);
    try {
      const data = await fetchReportJson(`${API_URL}/other_income_statement`, {
        from_date: inputValues.start_date,
        to_date: inputValues.end_date,
        income: inputValues.income,
      });
      setReport(data);
    } catch (error) {
      swal('Other Income Report', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!inputValues.start_date || !inputValues.end_date) {
      swal('Please select start and end dates', '', 'warning');
      return;
    }
    if (!inputValues.income?.length) {
      swal('Please select at least one income head', '', 'warning');
      return;
    }
    setPdfLoading(true);
    try {
      await downloadReportPdf(
        `${API_URL}/other-reports/other_income_statement/pdf`,
        {
          from_date: inputValues.start_date,
          to_date: inputValues.end_date,
          start_date: inputValues.start_date,
          end_date: inputValues.end_date,
          income: inputValues.income,
        },
        'other_income_statement.pdf',
      );
    } catch (error) {
      swal('Other Income Report', error.message, 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  const reportMetaLine = report
    ? formatReportDateRange(report.start_date, report.end_date)
    : '';
  const rowCount = report?.rows?.length ?? 0;

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        {isOtherReportsRoute ? (
          <Sidebar_Progress props={props} progress={22} />
        ) : (
          <Sidebar_Progress props={props} progress={14} operation={4} op={4} />
        )}
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title=" " />
          <OtherReportShell>
            <OtherReportPageHeader
              title="Other Income Statement"
              subtitle={meta.pump_name}
            />

            <OtherReportFilterCard>
              <OtherReportToolbar
                hintWide
                actions={(
                  <OtherReportActionButtons
                    loading={loading}
                    pdfLoading={pdfLoading}
                    onSubmit={loadReport}
                    onDownloadPdf={downloadPdf}
                  />
                )}
                dates={(
                  <ReportDateRangeFields
                    startDate={inputValues.start_date}
                    endDate={inputValues.end_date}
                    onChange={({ start_date, end_date }) => setInputValues({
                      ...inputValues,
                      start_date,
                      end_date,
                    })}
                  />
                )}
              />

              <OtherReportFilterSection
                title="Income heads"
                description="Select one or more non-sales income accounts to include."
              >
                <OtherReportSearchableCheckList
                  items={meta.income_heads || []}
                  selectedIds={inputValues.income || []}
                  onToggle={toggleIncome}
                  onChange={setIncome}
                  searchPlaceholder="Search income heads…"
                  getLabel={(head) => head.accounts_head}
                />
              </OtherReportFilterSection>
            </OtherReportFilterCard>

            <OtherReportResultsZone>
              {loading && <OtherReportLoader message="Generating report" />}

              {!loading && !report && <OtherReportPlaceholder />}

              {!loading && report && (
                <OtherReportDocument
                  title="Other Income Statement"
                  subtitle={report.pump_name}
                  meta={reportMetaLine}
                  rowCount={rowCount}
                >
                  {(report.rows || []).length > 0 ? (
                    <OtherReportTable
                      columns={[
                        { key: 'date', label: 'Date' },
                        { key: 'head', label: 'Account head' },
                        { key: 'description', label: 'Description' },
                        { key: 'amount', label: 'Amount', align: 'right' },
                      ]}
                      footer={(
                        <>
                          <span>Total income: {reportNumberFormat(report.amount_total)}</span>
                          <span>{rowCount} rows</span>
                        </>
                      )}
                    >
                      <tbody>
                        {(report.rows || []).map((row, index) => (
                          <tr key={index}>
                            <td>{formatReportDate(row.ac_date)}</td>
                            <td>{row.accounts_head}</td>
                            <td>{row.description}</td>
                            <td className="text-end">{reportNumberFormat(row.amount)}</td>
                          </tr>
                        ))}
                        <tr className="other-report-total-row">
                          <td colSpan="3" className="text-end">Total</td>
                          <td className="text-end">{reportNumberFormat(report.amount_total)}</td>
                        </tr>
                      </tbody>
                    </OtherReportTable>
                  ) : (
                    <OtherReportEmpty />
                  )}
                </OtherReportDocument>
              )}
            </OtherReportResultsZone>
          </OtherReportShell>
        </div>
      </div>
    </div>
  );
};

export default OtherIncomeReport;
